import React from 'react';
import pluralize from 'pluralize';
import { ApplyTagsFunction, TagsAppliedFunction } from '../../StudyList';
import {
  StudyListSidebar,
  StudyListSidebarSharedProps,
} from './StudyListSidebar';
import { TagsPanel } from './TagsPanel';
import { Toast, createToast } from 'components/core';
import { useTags } from 'hooks/reviews/useTags';
import { getReviewId } from 'query/review';
import { createTag } from 'query/review/Tags';

type Props = StudyListSidebarSharedProps & {
  disclosureId: string;
  selectedStudyIds: number[];
  onApplyTags: ApplyTagsFunction;
  onTagsApplied: TagsAppliedFunction;
  reviewId?: string;
};

export const TagsSidebar = ({
  disclosureId,
  selectedStudyIds,
  onApplyTags,
  onTagsApplied,
  reviewId,
  ...sidebarProps
}: Props) => {
  const { tags, status: tagsStatus, addTag, refetchTags } = useTags();
  const studiesSelectedCount = selectedStudyIds.length;

  const applyTags = async (reviewId: string, tagNames: string[]) => {
    try {
      await onApplyTags(reviewId, selectedStudyIds, tagNames);

      // Close before onTagsApplied clears the selection, so the sidebar exits showing its count
      sidebarProps.onClose();
      onTagsApplied(selectedStudyIds, tagNames);
    } catch (error) {
      createToast(
        <Toast type="danger">
          Oops! Something went wrong when applying tags
        </Toast>
      );
      // Rethrow so the tags panel stays open with the user's choices to retry
      throw error;
    }
  };

  const addTags = async (reviewId: string, tagName: string) => {
    try {
      const response = await createTag({ reviewId, tagName });

      await addTag({ id: response.id, name: response.name });
      return response;
    } catch (error) {
      if (error instanceof Error) {
        createToast(
          <Toast type="danger" role="alert">
            Error creating tag: {error.message}
          </Toast>
        );
      }
      // The name may already exist (a colleague created it), so show it to tick instead
      void refetchTags();
      // Rethrow so the panel doesn't select a tag that was never created
      throw error;
    }
  };

  const title = `Add ${pluralize('tag', studiesSelectedCount)} to ${pluralize(
    'study',
    studiesSelectedCount,
    true
  )}`;

  return (
    <StudyListSidebar
      {...sidebarProps}
      title={title}
      disclosureId={disclosureId}
      closeButtonLabel="Close tags"
      hasHeaderDivider
    >
      <TagsPanel
        reviewId={reviewId || getReviewId() || undefined}
        tags={tags}
        tagsStatus={tagsStatus}
        studiesSelectedCount={studiesSelectedCount}
        applyTags={applyTags}
        addTags={addTags}
      />
    </StudyListSidebar>
  );
};
